import React from 'react';

import { EventResultsSettings } from 'src/components/EventResultsSettings/EventResultsSettings';
import { Rule4Settings } from 'src/components/Rule4Settings/Rule4Settings';
import { DividendsSettings } from 'src/components/DividendsSettings/DividendsSettings';
import {
    EventResultsState,
    getMarkets,
} from 'src/domains/sportsbook/trading/view/eventDetails/eventResults/EventResults.state';
import { observer } from 'src/utils/mobx-react';
import styled from '@emotion/styled';
import { FilterTabs } from 'src/components/Filters/FilterTabs';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import {
    ButtonChangeInPlayForAll,
    ChangeInPlayPopup,
} from 'src/domains/sportsbook/trading/view/eventDetails/markets/MarketSettingsForm.style';
import { useAppStateContext } from 'src/AppState/AppState';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';

const EventResultsHeader = styled('div', { label: 'EventResultsHeader' })`
    height: 42px;
`;

const LoadingButton = styled('button', { label: 'LoadingButton' })`
    display: inline-block;
    position: relative;
    width: 65px;
    height: 55px;
`;

const PopupContent = styled('h4', { label: 'PopupContent' })`
    text-align: center;
    margin: 10px 0;
`;

interface EventResultsPropsType {
    eventResultsState: EventResultsState;
    marketId: MarketId;
}

const EventResultsMarket = observer('EventResultsMarket', (props: EventResultsPropsType): React.ReactElement | null => {
    const { eventResultsState, marketId } = props;

    const modelView = eventResultsState.getMarketModelView(marketId);
    const market = marketId.getModel();

    if (market === null) {
        return null;
    }

    const eventModel = market.getEvent();
    const status = eventResultsState.marketLabel(eventResultsState.filter);

    if (eventModel === null) {
        return null;
    }

    const eventId = eventModel.id2;

    const className = modelView.isOpen ? { className: 'event-results-table-row__opened' } : undefined;
    const hasTricasts = market.tricastsOffered;
    const hasForecasts = market.forecastsOffered;
    let tcLabel;
    if (hasTricasts && hasForecasts) {
        tcLabel = 'TC/FC';
    } else if (hasTricasts) {
        tcLabel = 'TC';
    } else if (hasForecasts) {
        tcLabel = 'FC';
    }

    const rule4 = market.getRawData().rule4 ?? null;
    const showRule4Button = rule4 !== null;

    const eachWay = market.eachWay?.terms ?? [];

    const row = (
        <tr
            key={marketId.key}
            {...className}
        >
            <td>
                <a
                    href='#'
                    onClick={(event): void => {
                        event.preventDefault();
                        modelView.toggleMarket();
                    }}
                >
                    {market.name}
                </a>
            </td>
            <td>{market.sp ? 'SP' : undefined}</td>
            <td>{eachWay.length > 0 ? 'EW' : undefined}</td>
            <td>{status}</td>
            <td>
                {showRule4Button ? (
                    <a
                        href='#'
                        className='event-results-badge green'
                        onClick={modelView.toogleR4}
                    >
                        R4
                    </a>
                ) : undefined}
                {tcLabel === undefined ? undefined : (
                    <a
                        href='#'
                        className='event-results-badge green'
                        onClick={modelView.toogleDividends}
                    >
                        {tcLabel}
                    </a>
                )}
            </td>
            <td />
        </tr>
    );

    if (modelView.isOpen) {
        const mode = modelView.mode;

        return (
            <>
                {row}
                <tr key={`${marketId.toString()}-expanded`}>
                    <td colSpan={6}>
                        {mode === 'r4' ? (
                            <Rule4Settings
                                key={marketId.key}
                                eventId={eventId}
                                marketId={marketId}
                            />
                        ) : mode === 'dividends' ? (
                            <DividendsSettings
                                key={marketId.key}
                                eventId={eventId}
                                marketId={marketId}
                            />
                        ) : (
                            <EventResultsSettings
                                key={marketId.key}
                                eventId={eventId}
                                marketId={marketId}
                                eventResultsState={eventResultsState}
                            />
                        )}
                    </td>
                </tr>
            </>
        );
    } else {
        return row;
    }
});

interface PropsType {
    eventId: EventId;
}

export const EventResults = observer('EventResults', (props: PropsType): React.ReactElement => {
    const appState = useAppStateContext();
    const [eventResultsState] = React.useState(() => new EventResultsState());
    const markets = getMarkets(props.eventId, appState);
    const selections = eventResultsState.getSelections(markets);

    let marketsEl;
    if (markets.length > 0) {
        marketsEl = (
            <table className='table event-results-table form'>
                <tbody>
                    {markets.map((marketId) => (
                        <EventResultsMarket
                            key={marketId.key}
                            eventResultsState={eventResultsState}
                            marketId={marketId}
                        />
                    ))}
                </tbody>
            </table>
        );
    } else {
        marketsEl = <div className='event-results-no-markets'>No available markets.</div>;
    }

    return (
        <div className='event-results'>
            <EventResultsHeader>
                <FilterTabs
                    available={eventResultsState.availableMarkets}
                    active={eventResultsState.filter}
                    className='event-results-filters'
                    onChange={eventResultsState.onChangeFilter}
                />
                <ul className='event-results-filters'>
                    <li className='form'>
                        <div
                            className='element checkbox'
                            style={{ margin: 0 }}
                        >
                            <label>
                                <input
                                    type='checkbox'
                                    checked={eventResultsState.shouldHideLosers}
                                    onChange={eventResultsState.toogleHideLosers}
                                />
                                <span>Hide losers</span>
                            </label>
                        </div>
                    </li>
                    <li className='refreshResults'></li>
                </ul>
                {eventResultsState.filter === 'open' ? (
                    <ButtonChangeInPlayForAll onClick={eventResultsState.openVoidPopup}>
                        Void the rest in open
                    </ButtonChangeInPlayForAll>
                ) : null}
            </EventResultsHeader>

            {marketsEl}
            {eventResultsState.isConfirmPopupOpened === true ? (
                <ChangeInPlayPopup className='popup'>
                    <h3>Void the rest in all open markets</h3>
                    <PopupContent>
                        In {markets.length} markets is {selections.length} selections with no result
                    </PopupContent>
                    <div>
                        <button
                            className='btn message__error'
                            onClick={eventResultsState.cancelVoidPopup}
                        >
                            Cancel
                        </button>
                        {eventResultsState.isSettlingInProgress ? (
                            <LoadingButton
                                className='btn'
                                disabled={true}
                            >
                                <Loader isActive={true} />
                            </LoadingButton>
                        ) : (
                            <button
                                className='btn'
                                onClick={() =>
                                    eventResultsState.setAllOpenMarketsVoidAndSettled(selections, props.eventId)
                                }
                            >
                                Confirm
                            </button>
                        )}
                    </div>
                </ChangeInPlayPopup>
            ) : null}
        </div>
    );
});
